/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/rodeo_round_configs-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-rodeo_round_configs-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/rodeo_round_configs-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, rodeo_id, event_id, performance_id, run_order_mode
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, rodeo_id, event_id, round_number, is_go_round, is_short_round, sort_order, created_at, updated_at, run_order_mode
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/rodeo_round_configs-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-rodeo_round_configs-api-client.ts (API client)
 * - client/api/generated_schemas/rodeo_round_configs.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-09-15T21:00:56.406Z
 * Source config: config/entities/rodeo_round_configs-entity-config.ts
 * Public fields: 16/16 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RodeoRoundConfigsClient, RodeoRoundConfigsQueryOptions } from '../generated_clients/generated-rodeo_round_configs-api-client.js';
import {
  RodeoRoundConfigsInsert,
  RodeoRoundConfigsUpdate,
  RodeoRoundConfigsFieldConfig
} from '../../types/generated_schemas/generated-rodeo_round_configs-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/rodeo_round_configs_business_hooks/

// Query keys with enhanced caching strategy
export const rodeoRoundConfigsKeys = {
  all: ['rodeo_round_configs'] as const,
  lists: () => [...rodeoRoundConfigsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...rodeoRoundConfigsKeys.lists(), { filters }] as const,
  details: () => [...rodeoRoundConfigsKeys.all, 'detail'] as const,
  detail: (id: string) => [...rodeoRoundConfigsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...rodeoRoundConfigsKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...rodeoRoundConfigsKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...rodeoRoundConfigsKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...rodeoRoundConfigsKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useRodeoRoundConfigsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new RodeoRoundConfigsClient();
};

/**
 * Hook to get all rodeo_round_configs with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with rodeo_round_configs data
 */
export const useRodeoRoundConfigsList = (options?: RodeoRoundConfigsQueryOptions) => {
  const client = useRodeoRoundConfigsClient();
  
  return useQuery({
    queryKey: rodeoRoundConfigsKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single rodeo_round_config by ID
 *
 * @param id - The rodeo_round_config ID
 * @returns React Query result with single rodeo_round_config data
 */
export const useRodeoRoundConfigs = (id: string) => {
  const client = useRodeoRoundConfigsClient();
  
  return useQuery({
    queryKey: rodeoRoundConfigsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/rodeo_round_configs
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/rodeo_round_configs_business_hooks/
 */

/**
 * Hook to create a new rodeo_round_config
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, rodeo_id, event_id, round_number, is_go_round, is_short_round, sort_order, created_at, updated_at, run_order_mode
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating rodeo_round_config
 */
export const useCreateRodeoRoundConfigs = () => {
  const client = useRodeoRoundConfigsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: RodeoRoundConfigsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rodeoRoundConfigsKeys.all });
    },
  });
};

/**
 * Hook to update an existing rodeo_round_config
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating rodeo_round_config
 */
export const useUpdateRodeoRoundConfigs = () => {
  const client = useRodeoRoundConfigsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RodeoRoundConfigsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: rodeoRoundConfigsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: rodeoRoundConfigsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a rodeo_round_config
 *
 * @returns React Query mutation for deleting rodeo_round_config
 */
export const useDeleteRodeoRoundConfigs = () => {
  const client = useRodeoRoundConfigsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rodeoRoundConfigsKeys.all });
    },
  });
};

/**
 * Hook to count rodeo_round_configs with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, rodeo_id, event_id, performance_id, run_order_mode
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useRodeoRoundConfigsCount = (filter?: Partial<Record<typeof RodeoRoundConfigsFieldConfig.filterable[number], any>>) => {
  const client = useRodeoRoundConfigsClient();
  
  return useQuery({
    queryKey: rodeoRoundConfigsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useRodeoRoundConfigsFieldConfig = () => {
  return RodeoRoundConfigsFieldConfig;
};